/**
 * Financing volume from the self-disclosure (Selbstauskunft 10, PLAN.md §3.9 `volume_calculation`, S4).
 *
 * All monthly amounts are cents. The assumptions come from `calculation_assumptions` and are stored as a
 * snapshot with every calculation ("Spätere Änderungen verändern diese Berechnung nicht").
 */
/** Seed values (PLAN.md §3.9; calculator keys owner decision 19.10.2026). */
export const DEFAULT_CALCULATION_ASSUMPTIONS = {
    livingExpensesCents: 90_000,
    rentCreditPct: 75,
    capPct: 45,
    interestPct: 4,
    repaymentPct: 2,
    calcInterestPct: 4.5,
    calcRepaymentPct: 1.5,
    calcFixedInterestYears: 10,
    calcFollowUpInterestPct: 4.5,
    calcFollowUpRepaymentPct: 1.5,
    calcRentIncreasePct: 15,
    calcRentIncreaseIntervalMonths: 36,
    calcAppreciationPct: 3,
    calcDepreciationPct: 2,
    calcTaxRatePct: 42,
    calcReservePerSqmCents: 60,
    calcOtherCostsPerSqmCents: 25,
};
/**
 * Selbstauskunft 10 (5.420 + 440 + 75 % × 1.180 = 6.745 €; − 900 − 1.240 − 0 = 4.605 €; cap 45 % = 3.035 €;
 * min ⇒ 3.035 €/month; annuity 6 % ⇒ 3.035 × 12 / 0.06 = 607.000 €).
 *
 * Installment figures are whole euros: fractions of a euro are dropped (3.035,25 € ⇒ 3.035 €), never
 * rounded up, so the volume is never overstated.
 */
export function calculateFinancingVolume(input, assumptions) {
    const creditedRentalIncomeCents = Math.round((input.rentalIncomeCents * assumptions.rentCreditPct) / 100);
    const countableIncomeCents = input.netIncomeCents + input.otherIncomeCents + creditedRentalIncomeCents;
    const liabilitiesCents = assumptions.livingExpensesCents +
        input.grossRentCents +
        input.leasingCents +
        (input.loansCents ?? 0) +
        (input.alimonyCents ?? 0);
    const disposableIncomeCents = countableIncomeCents - liabilitiesCents;
    const capLimitCents = floorToEuro(Math.max(0, (countableIncomeCents * assumptions.capPct) / 100));
    const affordableInstallmentCents = floorToEuro(Math.max(0, Math.min(disposableIncomeCents, capLimitCents)));
    const annuityPct = assumptions.interestPct + assumptions.repaymentPct;
    const volumeCents = annuityPct > 0 ? Math.round((affordableInstallmentCents * 12 * 100) / annuityPct) : 0;
    return {
        creditedRentalIncomeCents,
        countableIncomeCents,
        liabilitiesCents,
        disposableIncomeCents,
        capLimitCents,
        affordableInstallmentCents,
        annuityPct,
        volumeCents,
        assumptions: {
            livingExpensesCents: assumptions.livingExpensesCents,
            rentCreditPct: assumptions.rentCreditPct,
            capPct: assumptions.capPct,
            interestPct: assumptions.interestPct,
            repaymentPct: assumptions.repaymentPct,
        },
    };
}
function floorToEuro(cents) {
    return Math.floor(cents / 100) * 100;
}
//# sourceMappingURL=volume.js.map